import {
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { t } from 'i18next';
import React from 'react';
import { BLUE_GRAY_200, PRIMARY, GRAY_500 } from '../../constants/Colors';
import {
  LANGUAGES_ARRAY,
  STATES_ARRAY,
  CITIES_ARRAY,
  STORES_ARRAY,
  PRODUCTS_ARRAY,
  STATUS_ARRAY,
  ORDERS_ARRAY,
  PAYMENT_METHODS_ARRAY,
  PAYMENT_TERMS_ARRAY,
  SETUP_FEE_ARRAY,
  SUBSCRIPTION_PACK_ARRAY,
  TAXES_RATE_1_ARRAY,
  TAXES_RATE_2_ARRAY,
  SHIP_TO_ARRAY,
  YES_NO_ARRAY,
  SHIPPIING_CALCULATION_ARRAY,
  PREFERRED_SHIPPING_METHOD_ARRAY,
  SUPPLIER_ARRAY,
  RECIPE_BUILDER_ARRAY,
  VALUES,
  DATE_CREATED,
  PRODUCT_TYPE_ARRAY,
  PRIORITY_ARRAY,
  MESSAGE_STATUS_ARRAY,
  YES_NO_ARRAY_FR
} from '../../constants/Constants';
import { BakeasyAvatar } from '../common/BakeasyAvatar';
import i18n from '../../languages/languages';

interface BakeasySelectProps {
  label?: string;
  value?: any;
  disabled?: boolean;
  statusOptions?: string[];
  fullWidth?: boolean;
  size?: any;
  required?: boolean | false;
  placeholder?: string;
  label1?: string;
  renderValue?: ((selected: HTMLSelectElement) => JSX.Element | undefined) | undefined;
  onChange: (event: SelectChangeEvent<HTMLSelectElement>) => void;
  options?: {}[];
}

const selectSxProps: SxProps = {
  borderRadius: 2,
  '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: BLUE_GRAY_200
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1.5,
    borderColor: PRIMARY
  }
};

export function BakeasySelect(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ? props?.size : 'small'}
      fullWidth={props.fullWidth}
    >
      <InputLabel>{props.label} </InputLabel>
      <Select
        style={{ textAlign: 'start' }}
        key={props?.value}
        label={props.label}
        value={props.value}
        onChange={props.onChange}
        sx={selectSxProps}
      >
        {props?.statusOptions?.map(status => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelects(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <FormLabel sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}>
        {props?.label ? t(props?.label) : ''}
        {props?.required ? ' *' : ''}
      </FormLabel>

      <Select
        id={props?.label ? t(props?.label) : ''}
        disabled={props?.disabled}
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props?.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div style={{ color: GRAY_500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {props.placeholder ? props?.placeholder : t(props?.label || '')}
          </div>
        </MenuItem>
        {props?.options?.map((option: any, index: number) => (
          <MenuItem key={index} value={option?.id}>
            {t(option?.value)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectLabel(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ marginRight: 120 }}
      size={props?.size ? props?.size : 'medium'}
      fullWidth={props.fullWidth}
    >
      <FormLabel sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}>
        {props.label}
        {props?.required ? ' *' : ''}
      </FormLabel>

      <Select
        disabled={props?.disabled}
        style={{ textAlign: 'start' }}
        value={props.value}
        onChange={props.onChange}
        displayEmpty
        sx={selectSxProps}
        renderValue={(selected: any) => {
          if (selected?.length === 0) {
            return <div style={{ color: GRAY_500 }}>{props?.placeholder}</div>;
          }
          return selected;
        }}
      >
        <MenuItem value={''}>
          <div style={{ color: GRAY_500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {props?.placeholder}
          </div>
        </MenuItem>
        {props?.statusOptions?.map(status => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function Category(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <FormLabel sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('CATEGORY')}
        {props?.required ? ' *' : ''}
      </FormLabel>

      <Select
        id={props?.label ? t(props.label) : t('CATEGORY')}
        disabled={props?.disabled}
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div style={{ color: GRAY_500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {props?.placeholder ?? t('SELECT_CATEGORY')}
          </div>
        </MenuItem>
        {props?.options?.map((option: any, index: number) => (
          <MenuItem key={index} value={option?._id} disabled={option?.disabled}>
            {t(option?.categoryName)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectLabelLanguage(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <FormLabel sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('LANGUAGE')}
        {props?.required ? ' *' : ''}
      </FormLabel>

      <Select
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props?.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div style={{ color: GRAY_500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {props?.placeholder ?? t('SELECT_LANGUAGE')}
          </div>
        </MenuItem>
        {LANGUAGES_ARRAY?.map((option: any, index: any) => (
          <MenuItem key={index} value={option?.id}>
            {option?.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectLabelProfile(props: BakeasySelectProps) {
  let theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showSelect, setShowSelect] = React.useState(false);
  let savedAuth = localStorage.getItem('auth');
  let parsedAuth = JSON.parse(savedAuth!);

  return (
    <FormControl
      key={props?.label}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <Select
        key={props?.label}
        open={showSelect}
        onOpen={() => setShowSelect(true)}
        onClose={() => setShowSelect(false)}
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props.onChange}
        displayEmpty
        sx={selectSxProps}
        renderValue={(selected: any) => {
          if (selected?.length === 0) {
            return (
              <Grid container columnGap={1}>
                {isMobile && (
                  <BakeasyAvatar
                    height={24}
                    width={24}
                    value={parsedAuth?.userDetails?.userInfo?.name}
                  />
                )}
                {!isMobile && (
                  <BakeasyAvatar
                    height={24}
                    width={24}
                    value={parsedAuth?.userDetails?.userInfo?.name}
                  />
                )}
                {!isMobile && t('PROFILE_SETTINGS')}
              </Grid>
            );
          }
          return selected;
        }}
      >
        {props?.options?.map((item: any, index: number) => (
          <MenuItem
            key={index}
            sx={{ marginX: 0, marginY: 1 }}
            value={item?.id}
            onClick={(event: any) => {
              item.onClick(event);
              setShowSelect(false);
            }}
          >
            {item.icon}
            {t(item?.lable)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectState(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('STATE')} options={STATES_ARRAY} />;
}

export function BakeasySelectCity(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('CITY')} options={CITIES_ARRAY} />;
}

export function BakeasySelectStore(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('STORE')} options={STORES_ARRAY} />;
}

export function BakeasySelectProduct(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('PRODUCT')} options={PRODUCTS_ARRAY} />;
}

export function Status(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('STATUS')} options={STATUS_ARRAY} />;
}

export function Orders(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('ORDERS')} options={ORDERS_ARRAY} />;
}

export function BakeasySelectLanguage(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('LANGUAGE')} options={LANGUAGES_ARRAY} />;
}

export function BillTo(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('BILL_TO')} />;
}

export function PaymentMethod(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('METHOD')} options={PAYMENT_METHODS_ARRAY} />;
}

export function PaymentTerms(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('TERMS')} options={PAYMENT_TERMS_ARRAY} />;
}

export function SetupFee(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('SETUP_FEE')} options={SETUP_FEE_ARRAY} />;
}

export function SubscriptionPack(props: BakeasySelectProps) {
  return (
    <BakeasySelects {...props} label={t('SUBSCRIPTION_PACK')} options={SUBSCRIPTION_PACK_ARRAY} />
  );
}

export function TaxRateOne(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('RATE_1')} options={TAXES_RATE_1_ARRAY} />;
}

export function TaxRateTwo(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('RATE_2')} options={TAXES_RATE_2_ARRAY} />;
}

export function ShipTo(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('SHIP_TO')} options={SHIP_TO_ARRAY} />;
}

export function YesNo(props: BakeasySelectProps) {
  let options: any = i18n.language === 'fr' ? YES_NO_ARRAY_FR : YES_NO_ARRAY;
  return <BakeasySelects {...props} options={options} required={true} />;
}

export function ShippingCalculation(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      {...props}
      label={t('SHIPPING_CALCULATION')}
      required={false}
      options={SHIPPIING_CALCULATION_ARRAY}
    />
  );
}

export function PreferredShippingMethod(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      {...props}
      required={true}
      label={t('PREFERRED_SHIPPING_METHOD')}
      options={PREFERRED_SHIPPING_METHOD_ARRAY}
    />
  );
}

export function Supplier(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('SUPPLIER')} options={SUPPLIER_ARRAY} />;
}

export function SelectRecipeBuilder(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('RECIPE_BUILDER')} options={RECIPE_BUILDER_ARRAY} />;
}

export function Values(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('VALUES')} options={VALUES} />;
}

export function Date(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('DATE')} options={DATE_CREATED} />;
}

export function Priority(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('PRIORITY')} options={PRIORITY_ARRAY} />;
}

export function SelectProductType(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('PRODUCT_TYPE')} options={PRODUCT_TYPE_ARRAY} />;
}

export function MessageStatus(props: BakeasySelectProps) {
  return <BakeasySelects {...props} label={t('STATUS')} options={MESSAGE_STATUS_ARRAY} />;
}
